import jsPDF from "jspdf";
import "jspdf-autotable";
import { fixedPriceItems, pvcRollingShutterSizes, profileHandlePrices } from "../data/constants";

const usePdfGenerator = ({ 
  invoiceId, 
  customerInfo, 
  cabinets, 
  country, 
  grandTotal, 
  subtotal, 
  salesTax, 
  finalTotal 
}) => {
  const calculateCabinetTotal = (cab) => {
    if (country === "Shaker") {
      const cost = parseFloat(cab.unitCost) || 0;
      const qty = parseFloat(cab.qty) || 1;
      const totalCost = cost * qty;
      const discountPercent = parseFloat(cab.discount) || 0;
      const discountAmount = (totalCost * discountPercent) / 100;
      return Math.max(totalCost - discountAmount, 0);
    }

    // Check if it's Profile Handle
    if (cab.type === "Profile Handle") {
      const unitCost = parseFloat(cab.unitCost) || 0;
      const qty = parseFloat(cab.qty) || 1;
      
      // Profile Handle calculation (same as regular cabinets but without dimensions)
      const profileHandleCost = unitCost;
      const carcassPrice = 4860;
      
      // Calculate total cost before discount (multiply by 4 as per regular cabinet calculation)
      const totalCostBeforeDiscount = (profileHandleCost + carcassPrice) * 4 * qty;
      
      // Apply discount
      const discountPercent = parseFloat(cab.discount) || 0;
      const discountAmount = (totalCostBeforeDiscount * discountPercent) / 100;
      
      // Final total after discount
      const finalTotal = Math.max(totalCostBeforeDiscount - discountAmount, 0);
      
      // Convert to dollars (assuming 1 USD = 89 INR)
      return finalTotal / 89;
    }

    // Check if it's PVC Rolling Shutter
    if (cab.type === "PVC Rolling Shutter") {
      const unitCost = parseFloat(cab.unitCost) || 0;
      const qty = parseFloat(cab.qty) || 1;
      const totalCost = unitCost * qty;
      const discountPercent = parseFloat(cab.discount) || 0;
      const discountAmount = (totalCost * discountPercent) / 100;
      return Math.max(totalCost - discountAmount, 0);
    }

    // Check if it's a fixed price item
    const isFixedPriceItem = fixedPriceItems[cab.type];
    
    if (isFixedPriceItem) {
      // Fixed price item calculation
      const unitCost = fixedPriceItems[cab.type] || 0;
      const qty = parseFloat(cab.qty) || 1;
      const totalCost = unitCost * qty;
      const discountPercent = parseFloat(cab.discount) || 0;
      const discountAmount = (totalCost * discountPercent) / 100;
      return Math.max(totalCost - discountAmount, 0);
    }

    // Regular Custom Cabinet Calculation
    const height = parseFloat(cab.height) || 0;
    const width = parseFloat(cab.width) || 0;
    const unitCost = parseFloat(cab.unitCost) || 0;
    const qty = parseFloat(cab.qty) || 1;
    const drawerSlides = parseFloat(cab.drawerSlides) || 0;
    const hinges = parseFloat(cab.hinges) || 0;
    
    if (!cab.type || !height || !width || !cab.color || !unitCost) return 0;

    const heightInFeet = height / 12;
    const widthInFeet = width / 12;
    const sqft = heightInFeet * widthInFeet;
    const shutterCost = sqft * unitCost;
    const carcassPrice = 4860;
    const drawerSlidesCost = drawerSlides * 250;
    const hingesCost = hinges * 85;
    const totalCostBeforeDiscount = (shutterCost + carcassPrice + drawerSlidesCost + hingesCost) * 4 * qty;
    const discountPercent = parseFloat(cab.discount) || 0;
    const discountAmount = (totalCostBeforeDiscount * discountPercent) / 100;
    const finalTotal = Math.max(totalCostBeforeDiscount - discountAmount, 0);
    return finalTotal / 89;
  };

  const handleDownloadPDF = async () => {
    const logoPath = "/assets/Images/logoimg.png";
    const taxRate = 6.625;

    const invoiceNumber = invoiceId || "Invoice";
    const firstName = customerInfo.firstName || "Customer";
    const addressPart = customerInfo.address
      ? customerInfo.address.substring(0, 10).replace(/\s+/g, "_")
      : "Address";

    const doc = new jsPDF();
    let yPos = 20;

    // === HEADER ===
    doc.setTextColor(59, 130, 246);
    doc.setFontSize(14);
    doc.setFont(undefined, "bold");
    doc.text(`Invoice ID: ${invoiceId}`, 15, yPos);
    doc.text(`Cabinets: ${country}`, 15, yPos + 10);

    // Logo (right)
    try {
      const img = new Image();
      img.src = logoPath;
      await new Promise((res, rej) => {
        img.onload = res;
        img.onerror = rej;
      });
      doc.addImage(img, "PNG", 150, yPos - 5, 40, 0);
    } catch {
      console.log("Logo not loaded, continuing without image");
    }

    // Company Info below logo
    doc.setTextColor(0, 0, 0);
    doc.setFontSize(10);
    doc.setFont(undefined, "normal");
    doc.text("418 Rt 23, Franklin, NJ, 07416", 150, yPos + 10);
    doc.text("Phone: (862) 450-6069", 150, yPos + 15);

    yPos += 45;

    // === TITLE ===
    doc.setTextColor(59, 130, 246);
    doc.setFontSize(30);
    doc.text("Laminance Order", 105, yPos, { align: "center" });
    yPos += 20;

    // === CUSTOMER INFO ===
    doc.setFontSize(14);
    doc.setFont(undefined, "bold");
    doc.setTextColor(40, 40, 40);
    doc.text("CUSTOMER INFORMATION", 105, yPos, { align: "center" });
    yPos += 10;

    doc.setFontSize(11);
    doc.setFont(undefined, "normal");
    doc.setTextColor(60, 60, 60);

    const entries = [
      ["Customer Name", `${(customerInfo?.firstName || "").trim()} ${(customerInfo?.lastName || "").trim()}`.trim() || "N/A"],
      ["Phone", customerInfo?.phone || "N/A"],
      ["Email", customerInfo?.email || "N/A"],
      ["Date", customerInfo?.date || "N/A"],
      ["Address", customerInfo?.address || "N/A"],
      ["City", customerInfo?.city || "N/A"],
      ["State", customerInfo?.state || "N/A"],
      ["Zip Code", customerInfo?.zip || "N/A"],
    ];

    const leftLabelX = 20, leftValueX = 65;
    const rightLabelX = 120, rightValueX = 165;
    const valueWrapWidth = 45;
    const minRowGap = 8;

    for (let i = 0; i < entries.length; i += 2) {
      const [l1, v1] = entries[i];
      const v1Lines = doc.splitTextToSize(String(v1 ?? ""), valueWrapWidth);

      const rightPair = entries[i + 1];
      const l2 = rightPair ? rightPair[0] : "";
      const v2 = rightPair ? rightPair[1] : "";
      const v2Lines = rightPair ? doc.splitTextToSize(String(v2 ?? ""), valueWrapWidth) : [""];

      const rowHeight = Math.max(v1Lines.length, v2Lines.length) * 5;

      doc.setFont(undefined, "bold");
      doc.text(`${l1}:`, leftLabelX, yPos);
      doc.setFont(undefined, "normal");
      doc.text(v1Lines, leftValueX, yPos);

      if (l2) {
        doc.setFont(undefined, "bold");
        doc.text(`${l2}:`, rightLabelX, yPos);
        doc.setFont(undefined, "normal");
        doc.text(v2Lines, rightValueX, yPos);
      }

      yPos += Math.max(minRowGap, rowHeight);
    }

    yPos += 12;

    // === TABLE HEADER ===
    let tableHeaders, columnWidths;

    if (country === "Shaker") {
      tableHeaders = ["Type", "Description", "Unit Cost", "Qty", "Discount", "Total"];
      columnWidths = [25, 70, 25, 20, 25, 25];
    } else {
      tableHeaders = ["Type", "Width", "Height", "Color", "Material", "Qty", "Drawer", "Hinges", "Discount", "Total"];
      columnWidths = [20, 15, 15, 20, 25, 15, 15, 15, 15, 20];
    }

    doc.setFillColor(79, 70, 229);
    doc.rect(10, yPos, 190, 12, "F");
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(8);
    doc.setFont(undefined, "bold");

    let xPos = 12;
    tableHeaders.forEach((header, idx) => {
      doc.text(header, xPos, yPos + 8);
      xPos += columnWidths[idx];
    });

    yPos += 20;

    // === TABLE ROWS ===
    doc.setFontSize(7);
    doc.setTextColor(0, 0, 0);
    doc.setFont(undefined, "normal");

    cabinets.forEach((cabinet, rowIndex) => {
      if (yPos > 250) {
        doc.addPage();
        yPos = 20;
      }

      if (rowIndex % 2 === 0) {
        doc.setFillColor(248, 250, 252);
        doc.rect(10, yPos - 2, 190, 12, "F");
      }

      doc.setDrawColor(200, 200, 200);
      doc.rect(10, yPos - 2, 190, 12);

      xPos = 12;
      let rowData;

      if (country === "Shaker") {
        rowData = [
          cabinet.type || "N/A",
          cabinet.description ? cabinet.description.substring(0, 25) + (cabinet.description.length > 25 ? "..." : "") : "N/A",
          cabinet.unitCost ? `$${cabinet.unitCost}` : "N/A",
          cabinet.qty ? `${cabinet.qty}` : "N/A",
          cabinet.discount ? `${cabinet.discount}%` : "0%",
          `$${calculateCabinetTotal(cabinet).toFixed(2)}`,
        ];
      } else {
        const isFixedPriceItem = fixedPriceItems[cabinet.type];
        const isPVCRollingShutter = cabinet.type === "PVC Rolling Shutter";
        const isProfileHandle = cabinet.type === "Profile Handle";
        
        // For Profile Handle, show inches instead of width/height
        if (isProfileHandle) {
          rowData = [
            cabinet.type || "N/A",
            cabinet.profileHandleInches ? "Inches:" : "N/A",
            cabinet.profileHandleInches ? `${cabinet.profileHandleInches}"` : "N/A",
            "N/A", // Color
            cabinet.material || "N/A",
            cabinet.qty ? `${cabinet.qty}` : "N/A",
            "N/A", // Drawer Slides
            "N/A", // Hinges
            cabinet.discount ? `${cabinet.discount}%` : "0%",
            `$${calculateCabinetTotal(cabinet).toFixed(2)}`,
          ];
        }
        // For PVC Rolling Shutter, show size instead of width/height
        else if (isPVCRollingShutter) {
          rowData = [
            cabinet.type || "N/A",
            cabinet.pvcSize ? "Size:" : "N/A",
            cabinet.pvcSize || "N/A",
            cabinet.color || "N/A",
            cabinet.material || "N/A",
            cabinet.qty ? `${cabinet.qty}` : "N/A",
            "N/A", // Drawer Slides
            "N/A", // Hinges
            cabinet.discount ? `${cabinet.discount}%` : "0%",
            `$${calculateCabinetTotal(cabinet).toFixed(2)}`,
          ];
        } else {
          rowData = [
            cabinet.type || "N/A",
            isFixedPriceItem ? "N/A" : (cabinet.width ? `${cabinet.width}"` : "N/A"),
            isFixedPriceItem ? "N/A" : (cabinet.height ? `${cabinet.height}"` : "N/A"),
            isFixedPriceItem ? "N/A" : (cabinet.color || "N/A"),
            cabinet.material || "N/A",
            cabinet.qty ? `${cabinet.qty}` : "N/A",
            isFixedPriceItem ? "N/A" : (cabinet.drawerSlides || "0"),
            isFixedPriceItem ? "N/A" : (cabinet.hinges || "0"),
            cabinet.discount ? `${cabinet.discount}%` : "0%",
            `$${calculateCabinetTotal(cabinet).toFixed(2)}`,
          ];
        }
      }

      rowData.forEach((data, colIndex) => {
        // Handle long text for description in Shaker
        if (country === "Shaker" && colIndex === 1 && data.length > 25) {
          const shortText = data.substring(0, 25) + "...";
          doc.text(shortText, xPos, yPos + 8);
        } else {
          doc.text(String(data), xPos, yPos + 8);
        }
        xPos += columnWidths[colIndex];
      });

      yPos += 15;
    });

    yPos += 10;

    // === TOTALS & TAX BREAKDOWN ===
    doc.setFont(undefined, "bold");
    doc.setFontSize(10);
    
    // Subtotal
    doc.text(`Subtotal: $${subtotal.toFixed(2)}`, 15, yPos);
    yPos += 8;
    
    // Overall Discount
    if (customerInfo.discount > 0) {
      doc.text(`Overall Discount: ${customerInfo.discount || 0}%`, 15, yPos);
      yPos += 8;
      const discountAmount = subtotal * (customerInfo.discount / 100);
      doc.text(`Discount Amount: -$${discountAmount.toFixed(2)}`, 15, yPos);
      yPos += 8;
    }
    
    // Grand Total (Before Tax)
    doc.text(`Total (Before Tax): $${grandTotal.toFixed(2)}`, 15, yPos);
    yPos += 8;
    
    // Sales Tax
    doc.setTextColor(59, 130, 246);
    doc.text(`Sales Tax (${taxRate}%): +$${salesTax.toFixed(2)}`, 15, yPos);
    yPos += 8;
    
    // Final Total
    doc.setFontSize(12);
    doc.text(`Final Total: $${finalTotal.toFixed(2)}`, 15, yPos);
    yPos += 15;

    // === FOOTER ===
    doc.setTextColor(100, 100, 100);
    doc.setFontSize(8);
    doc.text("Generated on: " + new Date().toLocaleDateString(), 15, yPos);
    doc.text("Laminance Order System", 105, yPos, { align: "center" });

    // Save PDF
    const fileName = `${invoiceNumber}_${firstName}_${addressPart}.pdf`;
    doc.save(fileName);
  };

  return { handleDownloadPDF };
};

export default usePdfGenerator;